/**
 * Route for the Worktop — the user's personal arche board, which is itself an
 * arche (`arche_favorites`) rather than a top-level page. The id is not
 * self-describing, so link through this constant instead of the literal to keep
 * the platform Header and the focus-mode bars pointing at the same place.
 */
export const WORKTOP_PATH = '/a/arche_favorites';

/**
 * Canonical path segment for linking to an arche.
 *
 * Internal arches (IDs starting with `arche_`) use the archId so the
 * ArcheInteractionPage dispatch chain can match them by literal ID.
 * Plugin arches (IDs starting with `arc_`) use the slug for friendly URLs;
 * GenericArchePage resolves them via the metadata endpoint.
 *
 * This matches the two canonicalizing redirects in ConditionalProtectedRoute:
 * slug → `arche_` id for internal arches, and `arc_` id → slug for plugins
 * (`canonicalPluginArcheSegment`).
 */
export function archePath(arche: { archId: string; slug?: string | null }): string {
  if (arche.archId.startsWith('arche_')) {
    return arche.archId;
  }
  return arche.slug || arche.archId;
}

/** A plugin arche's canonical id: `arc_` + 32 lowercase hex. */
const PLUGIN_ARCHE_ID = /^arc_[0-9a-f]{32}$/;

/**
 * The shape a slug must have before the route guard will send the user to it.
 *
 * Generated slugs are kebab-case (`generateUniqueSlug`), so this admits every
 * slug the platform writes. It is checked anyway because the value becomes a
 * client-side navigation target: a `/`, `.` or `%` in it could steer the
 * redirect somewhere other than this arche's own page, and an underscore could
 * make it read as an arche ID — which the metadata route resolves BEFORE
 * slugs, so the slug URL might not lead back to this arche.
 */
const CANONICAL_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Where a plugin arche's URL segment should be rewritten to, or null when it is
 * already the one to show.
 *
 * Plugin arches are linked by slug (`archePath`), but several ways in only
 * know the id — the bridge's `open_arche`, the assistant's handoffs, a pasted
 * link — and used to leave the address bar on `/a/arc_<32hex>`. The guard
 * calls this with the URL segment and the metadata that segment resolved to:
 * when the segment IS the raw id and the arche has a usable slug, the slug is
 * the canonical segment.
 *
 * Anything else stays as it is: a slug URL (already canonical), an internal
 * `arche_` arche (whose canonical segment is its id — handled separately in
 * the guard), an arche with no slug, or a slug this function will not trust.
 */
export function canonicalPluginArcheSegment(
  segment: string,
  metadata: { archId: string; slug?: string | null },
): string | null {
  if (!PLUGIN_ARCHE_ID.test(segment) || metadata.archId !== segment) return null;
  const slug = metadata.slug;
  if (!slug || !CANONICAL_SLUG.test(slug)) return null;
  return slug;
}
